/**
 * Clara 360 - CSV Transaction Matching Service
 * Uses the central matching API for preview and processing
 *
 * v1.1 – Finance-Core Bridge + Learning Loop
 * Änderungen:
 *   - confirmMatches() sendet bestätigte Matches an finance-core (clara_tax)
 *   - Rückfluss: auto_booked, needs_review Counts für UI-Badge
 *   - Learning Loop: context_link wird nach IBAN-Bestätigung erweitert
 */
import { masterSupabase as supabase } from "../lib/supabase-master.js";

const API_BASE_URL = process.env.VITE_API_URL || "http://217.154.242.134:3001/api";

// Finance-Core auf VPS Control (Wahrheitsschicht)
const FINANCE_CORE_URL = "http://217.154.6.77:8100";
const FINANCE_CORE_TOKEN = "atlas_finance_2026";

/**
 * Get match preview for a list of transactions
 */
async function getMatchPreview(transactions) {
  try {
    const response = await fetch(`${API_BASE_URL}/matching/match-preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(transactions)
    });
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("❌ Preview matching error:", error);
    throw error;
  }
}

/**
 * Get matched transactions for review
 */
async function getMatchedTransactions(batchId) {
  try {
    const { data, error } = await supabase
      .from("unmatched_transactions")
      .select(`
        *,
        suggested_tenant:tenants!suggested_tenant_id(id, name, email),
        suggested_payment:rent_payments!suggested_payment_id(*)
      `)
      .eq("upload_batch_id", batchId)
      .not("suggested_payment_id", "is", null)
      .order("match_confidence", { ascending: false });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("❌ Error loading matches:", error);
    throw error;
  }
}

/**
 * Sendet bestätigte Matches an finance-core (clara_tax Wahrheitsschicht).
 * Gibt Rückfluss zurück: { auto_booked, needs_review, errors }
 */
async function pushToFinanceCore(confirmedMatches) {
  try {
    const response = await fetch(`${FINANCE_CORE_URL}/tools/payment/import_csv`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": FINANCE_CORE_TOKEN
      },
      body: JSON.stringify({
        matches: confirmedMatches,
        source: "csv_upload",
        user: "clara360"
      })
    });

    if (!response.ok) {
      const errText = await response.text();
      console.warn("⚠️ Finance-Core Bridge Fehler:", errText);
      return { auto_booked: 0, needs_review: 0, bridge_error: errText };
    }

    const result = await response.json();
    return {
      auto_booked: result.auto_booked || 0,
      needs_review: result.needs_review || 0,
      bridge_ok: true
    };
  } catch (error) {
    // Nicht-blockierend: Supabase-Update läuft trotzdem durch
    console.warn("⚠️ Finance-Core nicht erreichbar (non-blocking):", error.message);
    return { auto_booked: 0, needs_review: 0, bridge_error: error.message };
  }
}

/**
 * Confirm matches and update rent_payments.
 * Sendet danach an finance-core (clara_tax) und gibt Rückfluss zurück.
 */
async function confirmMatches(transactionIds) {
  try {
    // Transaktionen mit Vorschlägen laden
    const { data: transactions, error: txError } = await supabase
      .from("unmatched_transactions")
      .select("*")
      .in("id", transactionIds);
    
    if (txError) throw txError;
    
    let learnedCount = 0;
    const confirmedMatches = [];

    for (const tx of transactions) {
      if (!tx.suggested_payment_id) continue;
      
      // 1. Supabase rent_payment aktualisieren
      await supabase
        .from("rent_payments")
        .update({
          received_amount: tx.amount_abs,
          payment_date: tx.transaction_date,
          status: "paid"
        })
        .eq("id", tx.suggested_payment_id);
      
      // 2. Transaktion als gematcht markieren
      await supabase
        .from("unmatched_transactions")
        .update({ status: "matched" })
        .eq("id", tx.id);
      
      // 3. Learning Loop: IBAN lernen (Supabase RPC)
      if (tx.suggested_tenant_id && tx.sender_iban) {
        const { data: learned } = await supabase.rpc("learn_iban_from_match", {
          p_tenant_id: tx.suggested_tenant_id,
          p_transaction_id: tx.id
        });
        if (learned) learnedCount++;
      }

      // Match für Finance-Core Bridge aufbauen
      confirmedMatches.push({
        transaction_date: tx.transaction_date,
        amount: tx.amount_abs,
        sender_name: tx.sender_name,
        sender_iban: tx.sender_iban,
        reference: tx.reference,
        supabase_payment_id: tx.suggested_payment_id,
        supabase_tenant_id: tx.suggested_tenant_id,
        match_confidence: tx.match_confidence || 0.9,
        upload_batch_id: tx.upload_batch_id
      });
    }

    // 4. Finance-Core Bridge (clara_tax Wahrheitsschicht)
    const bridgeResult = await pushToFinanceCore(confirmedMatches);

    return { 
      confirmed: transactions.length,
      learned: learnedCount,
      // Rückfluss für UI-Badge
      auto_booked: bridgeResult.auto_booked,
      needs_review: bridgeResult.needs_review,
      bridge_ok: bridgeResult.bridge_ok || false,
      bridge_error: bridgeResult.bridge_error || null
    };
  } catch (error) {
    console.error("❌ Confirm error:", error);
    throw error;
  }
}

export default {
  getMatchPreview,
  getMatchedTransactions,
  confirmMatches
};
